import {
	type Reward,
	useMyReferralProfile,
} from "@src/components/referral/queries/get-my-referral-profile";
import {
	getReferralCheckoutTimestampMs,
	getReferralWelcomeShown,
	isWithinReferralCheckoutCooldown,
	removeStaleReferralCheckoutTimestampIfCooldownPassed,
} from "@src/utils/referral-welcome-storage";
import { useEffect } from "react";
import { useIntroVideosStepCompleted } from "./use-intro-videos-step-completed";

export const REFERRAL_WELCOME_POPUP_OPEN_DELAY_MS = 500;

export function getRefereeSignUpReward(
	userId: string,
	rewards: Reward[],
): Reward | undefined {
	return rewards.find(
		(reward) =>
			!reward.isRedeemed &&
			reward.refereeId === userId &&
			reward.rewardType === "Referee",
	);
}

type ReferralWelcomeVisibilityInput = {
	referredBy: string | null;
	userId: string;
	rewards: Reward[];
	introVideosCompleted: boolean;
	isWithinCheckoutCooldown: boolean;
	alreadyShown: boolean;
};

function computeReferralWelcomeVisibility(
	input: ReferralWelcomeVisibilityInput,
): boolean {
	const signUp = getRefereeSignUpReward(input.userId, input.rewards);
	return (
		!!input.referredBy &&
		signUp !== undefined &&
		input.introVideosCompleted &&
		!input.isWithinCheckoutCooldown &&
		!input.alreadyShown
	);
}

export function useReferralWelcomePopup(userId: string): {
	shouldShow: boolean;
	referredBy: string | null;
	referredByEmail: string | null;
	rewards: Reward[];
} {
	const introVideosCompleted = useIntroVideosStepCompleted();

	const { data: profile } = useMyReferralProfile();

	const referredBy = profile?.referredBy ?? null;
	const referredByEmail = profile?.referredByEmail ?? null;
	const rewards: Reward[] = profile?.rewards ?? [];

	const checkoutTsMs = getReferralCheckoutTimestampMs();
	const isWithinCheckoutCooldown = isWithinReferralCheckoutCooldown();

	useEffect(() => {
		if (checkoutTsMs !== null && !isWithinCheckoutCooldown) {
			removeStaleReferralCheckoutTimestampIfCooldownPassed();
		}
	}, [checkoutTsMs, isWithinCheckoutCooldown]);

	const alreadyShown = getReferralWelcomeShown();

	const shouldShow = computeReferralWelcomeVisibility({
		referredBy,
		userId,
		rewards,
		introVideosCompleted,
		isWithinCheckoutCooldown,
		alreadyShown,
	});

	return { shouldShow, referredBy, referredByEmail, rewards };
}
